/**
 * Core permission checking function.
 *
 * All permission checks in the application flow through this module.
 * Supports static role-based checks and an overrides layer for
 * per-pharmacy customization.
 */

import type {
  Role,
  PermissionAccess,
  PermissionResult,
  PermissionOverride,
} from "./types";
import type { Permission } from "./permissions";
import { PERMISSION_MATRIX } from "./permission-matrix";
import { APPROVER_ROLES } from "./roles";

/**
 * Central permission check function.
 *
 * Returns a discriminated union:
 * - `{ type: 'allowed' }` — action is permitted
 * - `{ type: 'denied', reason }` — action is blocked
 * - `{ type: 'requires_approval', reason, approverRoles }` — needs authorization
 */
export function checkPermission(
  role: Role,
  permission: Permission,
  _context: Record<string, unknown> = {},
  overrides?: PermissionOverride[]
): PermissionResult {
  // 1. Check overrides first (per-pharmacy customization)
  if (overrides && overrides.length > 0) {
    const override = overrides.find(
      (o) => o.role === role && o.permission === permission
    );
    if (override) {
      return resolveAccess(role, permission, override.access, overrides);
    }
  }

  // 2. Look up the static matrix
  const rolePermissions = PERMISSION_MATRIX[role];
  if (!rolePermissions) {
    return { type: "denied", reason: `Unknown role: ${role}` };
  }

  const rule = rolePermissions[permission];
  if (rule === undefined) {
    return {
      type: "denied",
      reason: `Unknown permission: ${permission}`,
    };
  }

  return resolveAccess(role, permission, rule, overrides);
}

function resolveAccess(
  role: Role,
  permission: Permission,
  access: string,
  overrides?: PermissionOverride[]
): PermissionResult {
  switch (access) {
    case "allowed":
      return { type: "allowed" };

    case "denied":
      return {
        type: "denied",
        reason: `Role '${role}' does not have permission '${permission}'`,
      };

    case "requires_approval":
      return {
        type: "requires_approval",
        reason: `Role '${role}' requires authorization for '${permission}'`,
        approverRoles: APPROVER_ROLES.filter((approverRole) => {
          const override = overrides?.find(
            (o) => o.role === approverRole && o.permission === permission
          );
          if (override) return override.access === "allowed";

          const approverRule = PERMISSION_MATRIX[approverRole]?.[permission];
          if (!approverRule) return false;
          return approverRule === "allowed";
        }),
      };

    default:
      return { type: "denied", reason: "Unknown access level" };
  }
}

/**
 * Resolve the effective permission for a role, including override info.
 */
export function resolveEffectivePermission(
  role: Role,
  permission: Permission,
  overrides?: PermissionOverride[]
): {
  access: PermissionAccess;
  isOverridden: boolean;
  defaultAccess: PermissionAccess;
} {
  const defaultAccess = PERMISSION_MATRIX[role]?.[permission] ?? "denied";

  if (overrides && overrides.length > 0) {
    const override = overrides.find(
      (o) => o.role === role && o.permission === permission
    );
    if (override) {
      return {
        access: override.access,
        isOverridden: true,
        defaultAccess,
      };
    }
  }

  return {
    access: defaultAccess,
    isOverridden: false,
    defaultAccess,
  };
}

/**
 * Simple boolean check — returns true only if fully allowed.
 */
export function isAllowed(
  role: Role,
  permission: Permission,
  context?: Record<string, unknown>,
  overrides?: PermissionOverride[]
): boolean {
  return (
    checkPermission(role, permission, context, overrides).type === "allowed"
  );
}

/**
 * Returns true if the permission requires approval (but is not denied).
 */
export function needsApproval(
  role: Role,
  permission: Permission,
  context?: Record<string, unknown>,
  overrides?: PermissionOverride[]
): boolean {
  return (
    checkPermission(role, permission, context, overrides).type ===
    "requires_approval"
  );
}

/**
 * Returns true if the permission is hard-denied (not approval-able).
 */
export function isDenied(
  role: Role,
  permission: Permission,
  context?: Record<string, unknown>,
  overrides?: PermissionOverride[]
): boolean {
  return (
    checkPermission(role, permission, context, overrides).type === "denied"
  );
}
